// =============================================================================
// src/views/camadas/camadas-controller.ts — Comportamento da Tela 05 (Camadas).
//
// Proveniencia: assets/unified-app.js Sections.camadas (linhas 1391-1502). Porte
//   FIEL de activate/_applyStyles/_syncPresetPills/_addIntensityMarkers/deactivate.
//   Estado vivo: activePreset (default 'voto') + activeOpacity (default 0.70).
//   Piso de opacidade 0.15 (Math.max no legado) preservado. NAO corrigir bugs
//   aparentes; desvios documentados no resumo.
//
// Reset de estilo: o legado faz `MapController.currentZoneStyleFn = null` no
//   deactivate. Esse campo e privado no MapController TS (fora do contrato), entao
//   usamos a mesma extensao local MapControllerStyleState ja adotada por
//   src/views/mapa-eleitoral/index.ts — preserva o reset exato sem alargar o contrato.
//
// SRP: orquestra estado + listeners da secao. Presets/cores em camadas-presets.ts;
//   marcadores de intensidade em camadas-intensity.ts; markup no index.ts.
// =============================================================================

import type { Map as LeafletMap, Marker, PathOptions } from 'leaflet';
import type { MapController, ZonaKey } from '../../app/types';
import { qs, qsa } from '../../app/dom';
import { bindOnce, setSelectedItem } from '../../core/ui';
import {
  CAMADAS_PRESETS,
  CAMADAS_DEFAULT_PRESET,
  CAMADAS_DEFAULT_OPACITY,
  CAMADAS_MIN_OPACITY,
  CAMADAS_FALLBACK_FILL,
  type CamadasPresetKey,
} from './camadas-presets';
import { addIntensityMarkers, removeIntensityMarkers } from './camadas-intensity';

/**
 * Campos privados de estilo que o legado zera no deactivate (currentZoneStyleFn).
 * Nao fazem parte do contrato publico do MapController; extensao local para
 * reproduzir o reset exato (mesmo padrao de src/views/mapa-eleitoral/index.ts).
 */
interface MapControllerZoneStyleState {
  currentZoneStyleFn: unknown;
}

/** Le o `data-preset` de um elemento e valida contra as chaves conhecidas. */
function readPresetKey(element: Element, fallback: CamadasPresetKey): CamadasPresetKey {
  const raw = (element as HTMLElement).dataset['preset'];
  return raw && raw in CAMADAS_PRESETS ? (raw as CamadasPresetKey) : fallback;
}

/** Le o valor inteiro de um slider de opacidade (parseInt base 10, 0 se invalido). */
function readSliderValue(slider: HTMLInputElement | null): number {
  /* v8 ignore next -- ambos chamadores passam slider não-nulo (ternário/forEach); guarda defensiva CLAUDE.md rule 11 */
  if (!slider) return 0;
  return parseInt(slider.value, 10) || 0;
}

/**
 * Controlador da secao Camadas. Mantem preset/opacidade correntes, aplica os
 * estilos de zona, sincroniza as pills e gerencia os marcadores de intensidade.
 * Espelha o objeto literal Sections.camadas com estado privado e metodos curtos.
 */
class CamadasStudioController {
  private activePreset: CamadasPresetKey = CAMADAS_DEFAULT_PRESET;
  private activeOpacity: number = CAMADAS_DEFAULT_OPACITY;
  private intensityMarkers: Marker[] = [];
  private activeMap: LeafletMap | null = null;

  /**
   * Ativa a secao (replica activate, 1402-1456): aplica estilos, adiciona os
   * rotulos de intensidade e liga os quatro grupos de listeners + belonging-config.
   */
  activate(mapCtrl: MapController): void {
    this.activeMap = mapCtrl.map;
    this.applyStyles(mapCtrl);
    this.intensityMarkers = addIntensityMarkers(mapCtrl);
    this.bindLayerItems(mapCtrl);
    this.bindEyeToggles();
    this.bindOpacitySliders(mapCtrl);
    this.bindPresetPills(mapCtrl);
    this.bindBelongingConfig();
  }

  /** Aplica o choropleth do preset corrente com a opacidade corrente (_applyStyles, 1458). */
  private applyStyles(mapCtrl: MapController): void {
    /* v8 ignore next -- activePreset sempre chave válida (readPresetKey valida); ?? voto defensivo CLAUDE.md rule 11 */
    const palette = CAMADAS_PRESETS[this.activePreset] ?? CAMADAS_PRESETS.voto;
    const opacity = this.activeOpacity;
    mapCtrl.setZoneStyles((zonaKey: ZonaKey): PathOptions => ({
      fillColor: palette[zonaKey] ?? CAMADAS_FALLBACK_FILL,
      fillOpacity: opacity,
      weight: 0.8,
      color: 'rgba(255,255,255,0.6)',
      opacity: 1,
    }));
  }

  /** Sincroniza pills de preset (.selected + .active) com o preset corrente (_syncPresetPills, 1466). */
  private syncPresetPills(): void {
    qsa('.u-preset-pill').forEach((pill) => {
      const matches = (pill as HTMLElement).dataset['preset'] === this.activePreset;
      pill.classList.toggle('selected', matches);
      pill.classList.toggle('active', matches);
    });
  }

  /** Clique numa camada (.u-layer-item): seleciona, le preset/opacidade do slider e reaplica. */
  private bindLayerItems(mapCtrl: MapController): void {
    qsa('.u-layer-item').forEach((item) => {
      bindOnce(item as HTMLElement, 'click', 'camadas-layer', () => this.onLayerItemClick(mapCtrl, item));
    });
  }

  /** Corpo do clique na camada (1408-1415). */
  private onLayerItemClick(mapCtrl: MapController, item: Element): void {
    setSelectedItem(qsa('.u-layer-item'), item);
    this.activePreset = readPresetKey(item, this.activePreset);
    const slider = qs<HTMLInputElement>('.u-opacity-slider', item);
    this.activeOpacity = slider ? Math.max(CAMADAS_MIN_OPACITY, readSliderValue(slider) / 100) : CAMADAS_DEFAULT_OPACITY;
    this.syncPresetPills();
    this.applyStyles(mapCtrl);
  }

  /** Liga o olho (.u-eye-toggle) de cada camada: alterna visibilidade/disabled (1418-1427). */
  private bindEyeToggles(): void {
    qsa('.u-eye-toggle').forEach((btn) => {
      bindOnce(btn as HTMLElement, 'click', 'camadas-eye', (e) => toggleEye(e, btn));
    });
  }

  /** Liga os sliders de opacidade (.u-opacity-slider): atualiza rotulo e reaplica se selecionado. */
  private bindOpacitySliders(mapCtrl: MapController): void {
    qsa<HTMLInputElement>('.u-opacity-slider').forEach((slider) => {
      bindOnce(slider, 'input', 'camadas-opacity', () => this.onOpacityInput(mapCtrl, slider));
    });
  }

  /** Corpo do input de opacidade (1430-1438). */
  private onOpacityInput(mapCtrl: MapController, slider: HTMLInputElement): void {
    const val = readSliderValue(slider);
    const valEl = slider.parentElement?.querySelector('.u-layer-opacity-val');
    if (valEl) valEl.textContent = `${val}%`;
    const item = slider.closest('.u-layer-item');
    if (item && item.classList.contains('selected')) {
      this.activeOpacity = Math.max(CAMADAS_MIN_OPACITY, val / 100);
      this.applyStyles(mapCtrl);
    }
  }

  /** Liga as pills de preset (.u-preset-pill): troca preset e re-seleciona a camada (1442-1449). */
  private bindPresetPills(mapCtrl: MapController): void {
    qsa('.u-preset-pill').forEach((pill) => {
      bindOnce(pill as HTMLElement, 'click', 'camadas-preset', () => this.onPresetPillClick(mapCtrl, pill));
    });
  }

  /**
   * Corpo do clique na pill de preset (1443-1448). O legado chama
   * UI.setSelected(itens, querySelector(...)), que pode ser null quando nenhuma
   * camada casa o preset — nesse caso todas ficam des-selecionadas. Reproduzimos
   * isso passando a propria camada casada ou, se ausente, marcando .selected=false
   * em todas (equivalente a `item === null`).
   */
  private onPresetPillClick(mapCtrl: MapController, pill: Element): void {
    this.activePreset = readPresetKey(pill, this.activePreset);
    const items = qsa('.u-layer-item');
    const matchingItem = qs(`.u-layer-item[data-preset="${this.activePreset}"]`);
    if (matchingItem) setSelectedItem(items, matchingItem);
    else items.forEach((item) => item.classList.remove('selected'));
    this.syncPresetPills();
    this.applyStyles(mapCtrl);
  }

  /** Liga o link "configurar" do pertencimento (.u-belonging-config) -> sendPrompt (1451-1455). */
  private bindBelongingConfig(): void {
    const config = qs('.u-belonging-config');
    bindOnce(config, 'click', 'camadas-belonging-config', (e) => {
      e.stopPropagation();
      window.sendPrompt('Configurar índice composto de pertencimento');
    });
  }

  /**
   * Desativa a secao (replica deactivate, 1496-1501): remove os rotulos de
   * intensidade e zera a funcao de estilo de zona do mapa.
   */
  deactivate(mapCtrl: MapController): void {
    removeIntensityMarkers(this.activeMap, this.intensityMarkers);
    this.intensityMarkers = [];
    (mapCtrl as MapController & MapControllerZoneStyleState).currentZoneStyleFn = null;
  }
}

/**
 * Alterna o icone do olho e o estado disabled da camada (corpo de _eye, 1419-1426).
 * Extraido para fora da classe por nao depender de estado do controlador.
 */
function toggleEye(e: Event, btn: Element): void {
  e.stopPropagation();
  const item = btn.closest('.u-layer-item');
  const off = btn.classList.toggle('off');
  btn.classList.toggle('ti-eye-off', off);
  btn.classList.toggle('ti-eye', !off);
  if (item) item.classList.toggle('disabled', off);
}

/** Instancia unica do controlador (espelha o singleton Sections.camadas). */
export const camadasController = new CamadasStudioController();
